import {
  Component,
  HostListener,
  Input,
  Output,
  EventEmitter,
  inject,
  OnInit,
  ChangeDetectionStrategy,
  DestroyRef,
  ChangeDetectorRef
} from '@angular/core';
import { NavigationExtras, Params, Router } from '@angular/router';
import { ProfileService } from '@shared/services/api/profile.service';
import { FsAttachmentService } from '@shared/services/api/fs-attachment.service';
import { take, tap } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ThemeService } from '../../../services/theme.service';
import { STATE_ICONS_MAPPING, TABLE_CONFIGURATION } from '../../models/constants';
import {
  ColumnConfig,
  EntityType,
  Roles,
  TableConfig,
  TableConfigurationType,
  TableCssConfig
} from '../../models/types';
import { TranslateService } from '@ngx-translate/core';
import { CompanyGroupUsersService } from '@shared/services/api/company-group-users.service';
import { AuthenticationService } from '@shared/services/authentication.service';

@Component({
  selector: 'app-table-row',
  templateUrl: './table-row.component.html',
  styleUrl: './table-row.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TableRowComponent implements OnInit {
  private _destroyRef = inject(DestroyRef);
  private _cdr = inject(ChangeDetectorRef);
  private _themeService = inject(ThemeService);
  private _fsAttachmentService = inject(FsAttachmentService);
  private _translateService = inject(TranslateService);
  private _router = inject(Router);
  private _profile = inject(ProfileService);
  private _companyGroupUsersService = inject(CompanyGroupUsersService);
  private _authService = inject(AuthenticationService);

  @Input() values!: EntityType;
  @Input() isHeading!: boolean;
  @Input() tableHeadings: (keyof EntityType)[] = [];
  @Input() columnConfig: ColumnConfig[] = [];
  @Input() hasButton = false;
  @Input() entityType = '';

  @Input() isOdd = true;
  @Input() isStripedTable = false;
  @Input() isSimple = false;
  @Input() headerPrefix = '';

  @Output() deleteRequested = new EventEmitter<any>();

  tableConfig!: TableConfig;
  cssConfig!: TableCssConfig;
  widths: number[] = [];
  isOpenDetail = false;
  tableHeadingsKeys: (keyof EntityType)[] = [];

  isLight?: boolean;
  userRole?: string;
  currentUserEmail?: string;
  currentUserCanImpersonate = false;

  iconsMappings = STATE_ICONS_MAPPING;

  serialNumber?: string;
  resolution?: string;
  description?: string;
  containsAttachment = false;

  constructor() {
    /* noop */
  }

  ngOnInit() {
    this.tableConfig = TABLE_CONFIGURATION[this.entityType as keyof TableConfigurationType];
    this.columnConfig = this.tableConfig.headings!;
    this.cssConfig = this.tableConfig.css;
    this.widths = this.cssConfig.table.column_widths;

    if (this.values && (this.values as any).serialNumber) {
      this.serialNumber = (this.values as any).serialNumber;
    }

    if (this.values && ['closed_tickets', 'requests'].includes(this.entityType)) {
      this.resolution = (this.values as any).resolution;
      this.description = (this.values as any).description;
    }

    if (this.values && ['open_tickets', 'closed_tickets', 'requests'].includes(this.entityType)) {
      this.containsAttachment = (this.values as any).containsAttachment;
    }

    this._themeService.isLight$
      .pipe(
        takeUntilDestroyed(this._destroyRef),
        tap((x) => {
          this.isLight = x;
          this._cdr.markForCheck();
        })
      )
      .subscribe();

    this._profile.profile$
      .pipe(
        take(1),
        tap((profile) => {
          this.userRole = profile.role;
          this.currentUserEmail = profile.email;
          this.currentUserCanImpersonate = profile.canImpersonate ?? false;
          this._cdr.markForCheck();
        })
      )
      .subscribe();
  }

  hasAdminRole(): boolean {
    return this.userRole === Roles.ADMIN;
  }

  isCurrentUserCompanyGroupRow(): boolean {
    return this.entityType === 'company_group_user' && this.currentUserEmail === (this.values as any)?.email;
  }

  shouldShowImpersonateButton(): boolean {
    if (this.entityType !== 'company_group_user') {
      return true;
    }
    // Mostra il bottone solo se l'utente loggato ha canImpersonate oppure è la propria riga (per mostrare "CONNECTED")
    return this.isCurrentUserCompanyGroupRow() || this.currentUserCanImpersonate;
  }

  shouldDisableImpersonateButton(): boolean {
    if (this.entityType !== 'company_group_user') {
      return false;
    }
    // Disabilitato se è la propria riga (non puoi impersonificare te stesso)
    // oppure se l'utente loggato non ha il permesso canImpersonate
    return this.isCurrentUserCompanyGroupRow() || !this.currentUserCanImpersonate;
  }

  handleBtnClick(values: any) {
    if (this.entityType === 'company_group_user') {
      // Blocca se è la propria riga o se l'utente loggato non ha il permesso
      if (this.isCurrentUserCompanyGroupRow() || !this.currentUserCanImpersonate) {
        return;
      }
      this.handleImpersonateClick(values);
      return;
    }

    if (this.tableConfig.btnAction === 'openDetails') {
      this.openDetails();
      return;
    }

    if (this.tableConfig.btnAction === 'download') {
      this.handleDownload(values);
      return;
    }

    if (this.tableConfig.route) {
      if (this.entityType === 'final_users') {
        this._profile.profile$.pipe(take(1)).subscribe((profile) => {
          if (this.tableConfig.onRouteClickCB) {
            this.tableConfig.onRouteClickCB(values, profile);
          }
        });
      }
      const routeArr = this.tableConfig.route.split('/').map((entry) => {
        if (entry.includes(':')) {
          entry = entry.replace(':', '');
          return this.values[entry as keyof EntityType];
        }
        return entry;
      });

      if (this.tableConfig.queryParams) {
        const queryParams: Params = {};
        for (const key of this.tableConfig.queryParams) {
          if (values[key] !== undefined) {
            queryParams[key] = values[key];
          }
        }
        const navigationExtras: NavigationExtras = {
          queryParams: queryParams
        };
        this._router.navigate(routeArr, navigationExtras);
      } else {
        this._router.navigate(routeArr);
      }
    }
  }

  handleImpersonateClick(values: any) {
    const targetUserId = values.id;
    if (!targetUserId) return;

    this._companyGroupUsersService.switchUser(targetUserId).subscribe({
      next: (response) => {
        localStorage.setItem('originalUserEmail', response.originalUserEmail);
        this._authService.signInWithCustomToken(response.customToken).subscribe({
          next: () => {
            this._router.navigate(['/home']).then(() => window.location.reload());
          },
          error: () => {
            localStorage.removeItem('originalUserEmail');
          }
        });
      },
      error: () => { /* noop */ }
    });
  }

  handleSecondaryBtnClick(values: any) {
    if (!this.tableConfig.hasSecondaryButton) {
      return;
    }

    if (this.isSecondaryButtonDisabled(values)) {
      return;
    }

    if (this.tableConfig.secondaryBtnAction === 'download') {
      this.handleSecondaryDownload(values);
      return;
    }

    if (this.tableConfig.secondaryBtnAction === 'routerLink' && this.tableConfig.secondaryRoute) {
      const routeArr = this.tableConfig.secondaryRoute.split('/').map((entry) => {
        if (entry.includes(':')) {
          entry = entry.replace(':', '');
          return this.values[entry as keyof EntityType];
        }
        return entry;
      });

      if (this.tableConfig.secondaryQueryParams) {
        const queryParams: Params = {};
        for (const key of this.tableConfig.secondaryQueryParams) {
          if (values[key] !== undefined) {
            queryParams[key] = values[key];
          }
        }
        const navigationExtras: NavigationExtras = {
          queryParams: queryParams
        };
        this._router.navigate(routeArr, navigationExtras);
      } else {
        this._router.navigate(routeArr);
      }
    }
  }

  isTertiaryButtonVisible(): boolean {
    return !!this.tableConfig.hasTertiaryButton && this.hasAdminRole();
  }

  handleTertiaryBtnClick(values: any) {
    if (!this.isTertiaryButtonVisible()) {
      return;
    }

    if (this.tableConfig.tertiaryBtnAction === 'delete') {
      this.deleteRequested.emit(values);
    }
  }

  private handleDownload(values: any) {
    if (!this.tableConfig.downloadIdProperty) {
      console.error('Download ID property not configured for this table');
      return;
    }

    const downloadId = values[this.tableConfig.downloadIdProperty];
    if (!downloadId) {
      console.error('Download ID not found in row data');
      return;
    }

    const filename = values['fileName'] || values['name'] || values['filename'] || undefined;

    this._fsAttachmentService.downloadFile(downloadId, filename).subscribe({
      next: () => {
        console.log('Download started successfully');
      },
      error: (error) => {
        console.error('Download failed:', error);
      }
    });
  }

  private handleSecondaryDownload(values: any) {
    if (!this.tableConfig.secondaryDownloadIdProperty) {
      console.error('Secondary download ID property not configured for this table');
      return;
    }

    const downloadId = values[this.tableConfig.secondaryDownloadIdProperty];
    if (!downloadId) {
      console.error('Secondary download ID not found in row data');
      return;
    }

    const filename = values['fileName'] || values['name'] || values['filename'] || undefined;

    this._fsAttachmentService.downloadFile(downloadId, filename).subscribe({
      next: () => {
        console.log('Secondary download started successfully');
      },
      error: (error) => {
        console.error('Secondary download failed:', error);
      }
    });
  }

  openDetails() {
    this.isOpenDetail = !this.isOpenDetail;
    this._cdr.markForCheck();
  }

  hoveredCellIndex: number | null = null;
  openUtilsCellIndex: number | null = null;
  hoveringPopoverIndex: number | null = null;

  onCellHover(index: number, entering: boolean, col: ColumnConfig) {
    const mode = col.utilsTriggerMode || 'both';
    if (!col.utils || col.utils.length === 0) return;
    if (mode === 'click') return;
    this.hoveredCellIndex = entering ? index : null;
  }

  onCellClick(index: number, col: ColumnConfig, event: Event) {
    if (!col.utils || col.utils.length === 0) return;
    const mode = col.utilsTriggerMode || 'both';
    if (mode === 'hover') return;
    event.stopPropagation();
    this.openUtilsCellIndex = this.openUtilsCellIndex === index ? null : index;
  }

  onPopoverEnter(index: number) {
    this.hoveringPopoverIndex = index;
  }

  onPopoverLeave(index: number) {
    if (this.hoveringPopoverIndex === index) {
      this.hoveringPopoverIndex = null;
    }
    if (this.hoveredCellIndex === index) {
      this.hoveredCellIndex = null;
    }
  }

  isUtilsVisible(index: number, col: ColumnConfig): boolean {
    if (!col.utils || col.utils.length === 0) return false;
    const mode = col.utilsTriggerMode || 'both';
    const hoverVisible = mode !== 'click' && this.hoveredCellIndex === index;
    const clickVisible = mode !== 'hover' && this.openUtilsCellIndex === index;
    const overPopover = this.hoveringPopoverIndex === index;
    return hoverVisible || clickVisible || overPopover;
  }

  @HostListener('document:click')
  closeOnOutsideClick() {
    this.openUtilsCellIndex = null;
  }

  handleUtil(util: any, row: any) {
    if (util?.showWhen && !util.showWhen(row)) return;
    if (util?.disabledWhen && util.disabledWhen(row)) return;

    const type = util?.actionType || (util?.onClick ? 'callback' : undefined);
    switch (type) {
      case 'callback':
        try {
          if (util?.onClick) {
            util.onClick(row);
          }
        } catch (e) {
          console.error('Utils callback error', e);
        }
        break;
      case 'routerLink': {
        if (!util.route) return;
        const routeArr = (util.route as string).split('/').map((entry: string) => {
          if (entry.includes(':')) {
            entry = entry.replace(':', '');
            return row[entry as keyof EntityType];
          }
          return entry;
        });
        if (util.queryParams) {
          const queryParams: Params = {};
          for (const key of util.queryParams) {
            if (row[key] !== undefined) queryParams[key] = row[key];
          }
          const navigationExtras: NavigationExtras = { queryParams };
          this._router.navigate(routeArr, navigationExtras);
        } else {
          this._router.navigate(routeArr);
        }
        break;
      }
      case 'download': {
        const idProp = util.downloadIdProperty;
        if (!idProp) return;
        const downloadId = row[idProp];
        if (!downloadId) return;
        const filename = row['fileName'] || row['name'] || row['filename'] || undefined;
        this._fsAttachmentService.downloadFile(downloadId, filename).subscribe({
          next: () => {
            /* noop */
          },
          error: (err) => console.error('Utils download failed', err)
        });
        break;
      }
      default:
        break;
    }

    this.openUtilsCellIndex = null;
  }

  joinValues(values: any, displayType = 'comma-separated') {
    switch (displayType) {
      case 'comma-separated':
        return values.join(', ');
      case 'bullet-list':
        return '• ' + values.join('\n• ');
      case 'numbered-list':
        return values.map((value: string, index: number) => `${index + 1}. ${value}`).join('\n');
      default:
        return values.join(', ');
    }
  }

  getArrayFieldValues(key: any, displayType = 'comma-separated', needsTranslation = false): string {
    if (typeof key === 'string' && key.includes('.')) {
      const [parentKey, childKey] = key.split('.');
      const arrayData = (this.values as any)[parentKey];
      if (!arrayData || !Array.isArray(arrayData) || arrayData.length === 0) {
        return 'ND';
      }
      let values: string[] = [];
      if (childKey === 'accountName') {
        const uniqueAccounts = new Map<string, string>();
        arrayData.forEach((item) => {
          if (item.accountId && item.accountName && !uniqueAccounts.has(item.accountId)) {
            uniqueAccounts.set(item.accountId, item.accountName);
          }
        });
        values = Array.from(uniqueAccounts.values());
      } else {
        values = arrayData.map((item) => item[childKey]).filter(Boolean);
      }
      if (values.length === 0) {
        return 'ND';
      }
      if (values.length > 4) {
        const displayValues = values.slice(0, 4);
        const displayText = displayValues.join(', ') + ` (+${values.length - 4})`;
        this.storeTooltipData(key, values.join(', '));
        return displayText;
      }
      return this.joinValues(values, displayType);
    }
    const value = (this.values as any)[key];

    if (needsTranslation && Array.isArray(value)) {
      const translatedValues = value.map((item) => this._translateService.instant(item));
      return this.joinValues(translatedValues, displayType);
    }

    return Array.isArray(value) && value.length === 0 ? 'ND' : value || 'ND';
  }

  tooltipData = new Map<string, string>();

  private storeTooltipData(key: string, value: string): void {
    this.tooltipData.set(key, value);
  }

  hasTooltip(key: string): boolean {
    return this.tooltipData.has(key);
  }

  getTooltip(key: string): string {
    return this.tooltipData.get(key) || '';
  }

  getFullArrayValues(key: any): string {
    if (typeof key === 'string' && key.includes('.')) {
      const [parentKey, childKey] = key.split('.');
      const arrayData = (this.values as any)[parentKey];
      if (!arrayData || !Array.isArray(arrayData) || arrayData.length === 0) {
        return 'ND';
      }
      let values: string[] = [];
      if (childKey === 'accountName') {
        const uniqueAccounts = new Map<string, string>();
        arrayData.forEach((item) => {
          if (item.accountId && item.accountName && !uniqueAccounts.has(item.accountId)) {
            uniqueAccounts.set(item.accountId, item.accountName);
          }
        });
        values = Array.from(uniqueAccounts.values());
      } else {
        values = arrayData.map((item) => item[childKey]).filter(Boolean);
      }
      if (values.length === 0) {
        return 'ND';
      }
      return values.join(', ');
    }
    const value = (this.values as any)[key];
    return Array.isArray(value) && value.length === 0 ? 'ND' : value || 'ND';
  }

  isBoolean(value: any): boolean {
    return typeof value === 'boolean';
  }

  isSecondaryButtonDisabled(values: any): boolean {
    if (this.tableConfig.secondaryBtnAction === 'download' && this.tableConfig.secondaryDownloadIdProperty) {
      const downloadId = values[this.tableConfig.secondaryDownloadIdProperty];
      return !downloadId;
    }
    return false;
  }

  private resolveMaxChars(truncate: any): number | null {
    if (!truncate || truncate.maxChars === undefined || truncate.maxChars === null) {
      return null;
    }

    const mc = truncate.maxChars;

    if (typeof mc === 'number') {
      return mc;
    }

    if (typeof mc === 'object') {
      const defaultValue = (mc as any).default;
      if (typeof defaultValue !== 'number') {
        console.error('truncate.maxChars object is missing required "default" number');
        return null;
      }

      const breakpoints = ['xs', 'sm', 'md', 'lg', 'xl'];
      const provided: number[] = [];
      for (const bp of breakpoints) {
        const v = (mc as any)[bp];
        if (typeof v === 'number' && !isNaN(v)) {
          provided.push(v);
        }
      }

      if (provided.length === 0) {
        return defaultValue;
      }

      return Math.max(...provided);
    }

    return null;
  }

  getTruncateMaxChars(truncate: any): string | null {
    const n = this.resolveMaxChars(truncate);
    return n ? n + 'ch' : null;
  }

  getComposedHeaderText(h: ColumnConfig, headerPrefix: string): string {
    if (!h.compose) return '';
    const translatePrefix = 'TABLE.HEADINGS.' + headerPrefix + (headerPrefix ? '.' : '');
    const parts: Record<string, string> = {};
    for (const k of h.compose.keys) {
      const label = this._translateService.instant(translatePrefix + (k as string));
      parts[k as string] = label;
    }
    parts['COMMON.CONJUNCTION.AND'] = this._translateService.instant('COMMON.CONJUNCTION.AND');
    if (h.compose.headerTemplate) {
      return this.evaluateTemplate(h.compose.headerTemplate, parts, h.compose.trimResult !== false);
    }
    const conj = this._translateService.instant('COMMON.CONJUNCTION.AND');
    const labels = h.compose.keys.map((k) => parts[k as string]).filter(Boolean);
    if (labels.length === 0) return '';
    if (labels.length === 1) return labels[0];
    return `${labels[0]} ${conj} ${labels.slice(1).join(' ')}`.trim();
  }

  getComposedCellText(h: ColumnConfig): string {
    if (!h.compose) return '';
    const ctx: Record<string, any> = {};
    for (const k of h.compose.keys) {
      const v = this.getValueByPath(this.values as any, k as string);
      ctx[k as string] = Array.isArray(v) ? this.joinValues(v, h.arrayDisplayType || 'comma-separated') : v;
    }
    const result = this.evaluateTemplate(h.compose.template, ctx, h.compose.trimResult !== false);
    return result || this._translateService.instant(h.defaultMessaggeIfEmpty || 'STATUSES.NOT_AVAILABLE');
  }

  private getValueByPath(obj: any, path: string): any {
    if (!path.includes('.')) return obj?.[path];
    return path.split('.').reduce((acc: any, key: string) => (acc ? acc[key] : undefined), obj);
  }

  private evaluateTemplate(template: string, context: Record<string, any>, trim = true): string {
    const out = template.replace(/\$\{([^}]+)\}/g, (_, name) => {
      const v = context[name.trim()];
      return v === undefined || v === null ? '' : String(v);
    });
    return trim ? out.trim() : out;
  }
}
